export interface User {
	id: number;
	creationDate: string;
	abstractIDUserUUID: string;
	displayName: string;
	email: string;
	isAdmin: boolean;
	roles: { name: string; roleUUID: string }[];
	lastLogin: string;
}

export interface AuditLog {
	id: number;
	creationDate: string;
	action:
		| 'USER_REGISTERED'
		| 'USER_LOGIN'
		| 'ROLE_CHANGED'
		| 'SETTINGS_CHANGED'
		| 'USER_UPDATED'
		| 'CHECKOUT_STARTED'
		| 'CHECKOUT_RETAXED'
		| 'PURCHASE_COMPLETED'
		| 'SUBSCRIPTION_CREATED'
		| 'SUBSCRIPTION_CHANGED'
		| 'SUBSCRIPTION_CANCELED'
	| 'SUBSCRIPTION_CANCEL_FAILED'
		| 'SUBSCRIPTION_RESUMED'
	| 'SUBSCRIPTION_TAX_RATE_UPDATED'
	| 'SELLER_UPDATED'
	| 'INVOICE_DOCUMENT_ISSUED'
	| 'CREDIT_NOTE_ISSUED'
	| 'EMAIL_ATTACHMENT_FAILED'
	| 'CLICKWRAP_UPDATED'
	| 'CLICKWRAP_DELETED'
	| 'CLICKWRAP_ACCEPTED'
	| 'PRODUCT_OPTION_UPDATED'
	| 'PRODUCT_OPTION_DELETED'
		| 'PAYMENT_FAILED'
		| 'PAYMENT_RECOVERED'
		| 'REFUND_ISSUED'
		| 'REFUND_FAILED'
		| 'CHARGEBACK_RECEIVED'
		| 'DISPUTE_CLOSED'
		| 'ENTITLEMENT_GRANTED'
		| 'ENTITLEMENT_REVOKED'
		| 'MANUAL_ENTITLEMENT_GRANTED'
		| 'MANUAL_ENTITLEMENT_REVOKED'
		| 'PRODUCT_CREATED'
		| 'PRODUCT_UPDATED'
		| 'PRICE_CREATED'
		| 'PRICE_ARCHIVED'
		| 'COUPON_CREATED'
		| 'PRODUCT_FAMILY_CREATED'
		| 'PRODUCT_FAMILY_UPDATED'
	| 'PRODUCT_FAMILY_ARCHIVED'
	| 'MEDIA_UPLOADED'
		| 'PRICE_UPDATED'
		| 'COUPON_UPDATED'
		| 'APPLICATION_REGISTERED'
		| 'APPLICATION_UPDATED'
		| 'API_KEY_ROTATED'
		| 'WEBHOOK_SECRET_ROTATED'
		| 'WEBHOOK_DELIVERY_DEAD'
		| 'WEBHOOK_DELIVERY_REPLAYED'
		| 'WEBHOOK_DELIVERY_RETRIED'
		| 'MIGRATION_BATCH_STAGED'
		| 'MIGRATION_MAPPED'
		| 'MIGRATION_DRY_RUN'
		| 'MIGRATION_APPLIED'
		| 'MIGRATION_BATCH_DISCARDED'
		| 'MIGRATION_HISTORY_ATTACHED'
		| 'MIGRATION_DOCUMENTS_IMPORTED'
		| 'MIGRATION_BLOCKING_OVERRIDDEN'
		| 'MIGRATION_PROFILES_UNAVAILABLE'
		| 'MIGRATION_REVERTED'
		| 'MIGRATION_APPLICATIONS_NOTIFIED'
		| 'PAYMENT_PROVIDER_CREATED'
		| 'PAYMENT_PROVIDER_UPDATED'
		| 'PAYMENT_PROVIDER_DEFAULT_CHANGED'
		| 'TEST_CLOCK_CREATED'
		| 'TEST_CLOCK_ADVANCED'
		| 'TEST_CLOCK_DELETED'
		| 'SUBSCRIPTION_PAYMENT_METHOD_CHANGED'
		| 'PROVIDER_CREDENTIALS_ROTATED'
		| 'CURRENCY_UPDATED'
		| 'REFUND_POLICY_UPDATED'
		| 'EMAIL_PROVIDER_UPDATED'
		| 'EMAIL_TEMPLATE_UPDATED'
		| 'EMAIL_EXPIRED'
		| 'API_KEY_AUTH_FAILED'
		| 'CATALOG_SYNCED'
		| 'RECONCILIATION_ANOMALY'
		| 'RECONCILIATION_COMPLETED';
	userID: number;
	targetUserID?: number;
	/**
	 * Who the actor is **today**, resolved server-side per page rather than stored on the row.
	 *
	 * Null where the users table can no longer name them, which is ordinary: `userID` carries no
	 * foreign key so that the trail outlives the users in it (§15). The screen falls back to the
	 * id, which still identifies one actor across entries.
	 */
	userName?: string | null;
	targetUserName?: string | null;
	details: Record<string, unknown>;
	ipAddress: string;
}

/**
 * What `/api/configuration` hands back before anything renders (Design §13).
 *
 * `currencies` carries **whole rows**, not ISO codes: a client given only codes has no choice but
 * to hard-code ×100, which is the silent zero-decimal bug §10 exists to prevent. Every figure the
 * storefront shows is formatted from one of these rows, through `PriceDisplay`.
 */
export interface Configuration {
	name: string;
	version: string;
	/** False while any readiness condition is unmet — Buy renders disabled rather than 500ing. */
	isOpen: boolean;
	supportEmail: string;
	currencies: StorefrontCurrency[];
	/** Server-resolved: the customer's pin if they have one, else the store default (§10). */
	suggestedCurrency: string | null;
	/**
	 * Whether checkout will have to ask this caller where they are buying from (§10.1).
	 *
	 * Resolved **per caller**: false the moment the store holds a country for them, which is every
	 * purchase after the first, and false outright wherever the provider computes the tax — every
	 * store's default. It is an advance warning so the confirmation window can put the field beside
	 * the price; `createSession` refuses on its own regardless, so a stale `false` costs a refusal
	 * the checkout page still knows how to ask about rather than an unpriced sale.
	 */
	billingCountryRequired: boolean;
	/**
	 * Whether the storefront should collect behaviour at all (§12.5).
	 *
	 * The switch governs **both ends**, which is why it is shipped rather than left to the endpoint:
	 * with only the server half a page already open keeps sending batches that are silently dropped,
	 * and with only the client half a stale bundle keeps writing rows after collection was turned
	 * off. Neither failure surfaces anywhere.
	 */
	analyticsEnabled: boolean;
	/**
	 * The billing identity the store already holds for this caller (§10.1), so a form that asks
	 * for it never asks blank. Null anonymous, and null where nothing is on file.
	 *
	 * Not the same question as `billingCountryRequired`, which goes false the moment a country is
	 * known: these are for the case where the field comes back **despite** one being known — a
	 * `tax.*` refusal, where the country on file is one the store cannot price in and the customer
	 * is being asked to correct it. A picker rendered empty there makes them guess which country
	 * the store thinks they are in.
	 *
	 * Optional, because a server that predates them sends neither and `undefined` must not be
	 * written into a field as the word itself.
	 */
	billingCountry?: string | null;
	billingCompany?: string | null;
	/** The caller's professional-individual declaration (§10.1), so the box opens as they left it. */
	billingProfessional?: boolean;
	/**
	 * The countries where a declared professional with no company is charged as a business (§10.1).
	 * Checkout offers the box only there; empty while the provider calculates tax.
	 */
	professionalCountries?: string[];
}

/** The public subset of a `Currency` row. Everything a formatter needs, and nothing else. */
export interface StorefrontCurrency {
	code: string;
	name: string;
	symbol: string;
	/** 2 for USD, **0 for JPY**. Never assume two, or ¥1,200 renders as ¥120,000. */
	minorUnitDigits: number;
	roundingIncrement: number;
	displayOrder: number;
	isDefault: boolean;
}

export interface PaginatedResponse<T> {
	items: T[];
	total: number;
}

/** Endpoints that return a whole collection rather than a page of one. */
export interface ListResponse<T> {
	items: T[];
}

// --- Configuration (Design §4.5) -------------------------------------------------------------
//
// Everything below is *generated from* rather than *mirrored by* the client: `SettingsForm` and
// `ProviderCredentialsForm` render whatever the server declares, so adding a setting or a
// payment adapter is a server-side change with no client work.

/** The coarse type the form maps to a control. Declared by the server's `SettingsRegistry`. */
export type SettingType =
	| 'string'
	| 'url'
	| 'email'
	| 'int'
	| 'boolean'
	| 'intList'
	| 'stringList'
	/** A subset of the descriptor's `choices`, rendered as checkboxes by `SettingsForm`. */
	| 'choiceList'
	/** A string that is an ISO-3166 code. `SettingsForm` renders `CountrySelect` for it (§4.5). */
	| 'country';

/**
 * Which resolution tier the effective value came from. `environment` renders read-only with the
 * variable named; `database-invalid` renders as an error with a Reset action.
 */
export type SettingSource = 'default' | 'database' | 'environment' | 'database-invalid';

export interface SettingDescriptor {
	key: string;
	type: SettingType;
	description: string;
	/** The effective value. Masked when `isSecret` — the plaintext is never sent (§12). */
	value: unknown;
	defaultValue: unknown;
	source: SettingSource;
	isSecret: boolean;
	isRequired: boolean;
	/** Set by the environment escape hatch: the form renders the row read-only. */
	isLocked: boolean;
	environmentVariable: string | null;
	/** Why a `database-invalid` row was rejected, so the form can explain and offer Reset. */
	error: string | null;
	/** The declared range, for `int` and for each element of an `intList`. */
	minimum: number | null;
	maximum: number | null;
	/** For a `choiceList`: what may be ticked, with each checkbox's label. Null for every other type. */
	choices?: { value: string; label: string }[] | null;
}

/** One field an adapter needs configured, from its own `describeCredentials()`. */
export interface CredentialFieldSpec {
	name: string;
	label: string;
	isSecret: boolean;
	isRequired: boolean;
	placeholder: string | null;
	help: string | null;
}

/**
 * A non-secret adapter option. Readable, defaulted, and generated into the same form — an option
 * that can only be set with `curl` is one an operator cannot use to unbreak checkout.
 */
export interface OptionFieldSpec {
	name: string;
	label: string;
	type: 'boolean' | 'string';
	default: boolean | string;
	help: string | null;
}

export interface ProviderTypeDescriptor {
	type: string;
	displayName: string;
	credentialFields: CredentialFieldSpec[];
	optionFields: OptionFieldSpec[];
}

/**
 * The email adapter's half of the same contract.
 *
 * Separate from `ProviderTypeDescriptor` rather than reusing it because an email adapter declares
 * **no** options — `describeCredentials()` and nothing else. Sharing the payment shape would make
 * the client type wider than what `/api/admin/email-provider/types` actually returns, and a form
 * written against the wider type would be reading a field the server never sends.
 */
export interface EmailProviderTypeDescriptor {
	/** The adapter key, narrowed to the union the write endpoints accept. */
	type: EmailProviderType;
	displayName: string;
	credentialFields: CredentialFieldSpec[];
}

export type PaymentProviderMode = 'test' | 'live';
export type ProviderHealthStatus = 'unknown' | 'healthy' | 'unhealthy' | 'undecryptable';

/** Note what is absent: there is no `credentials` field, and there never will be (§12). */
export interface PaymentProviderConfig {
	uuid: string;
	type: string;
	name: string;
	mode: PaymentProviderMode;
	isActive: boolean;
	isDefault: boolean;
	options: Record<string, unknown>;
	/** Field name → `sk_live_••••4242`, computed server-side at write time. */
	credentialMasks: Record<string, string>;
	healthStatus: ProviderHealthStatus;
	healthError: string | null;
	healthCheckedAt: string | null;
	/** Null while `store.publicURL` is unset — there is no URL worth pasting yet. */
	webhookURL: string | null;
	creationDate: string;
	updatedAt: string;
}

export interface Currency {
	code: string;
	name: string;
	symbol: string;
	/** 2 for USD, **0 for JPY**. Never assume two, or a zero-decimal currency is charged 100×. */
	minorUnitDigits: number;
	isEnabled: boolean;
	isDefault: boolean;
	displayOrder: number;
	roundingIncrement: number;
	creationDate: string;
	updatedAt: string;
}

/** One thing standing between the store and being able to take money. */
export interface UnmetCondition {
	condition: string;
	message: string;
}

/**
 * One worker's heartbeat (Design §3.2).
 *
 * Worker failure is invisible by construction: §6.2's inline reconcile keeps checkout succeeding,
 * so purchases complete while renewals, cancellations, entitlement delivery, email and
 * reconciliation quietly do nothing. `isStale` is the only thing that says so.
 */
export interface WorkerHealth {
	workerName: string;
	instanceID: string;
	lastRunAt: string;
	lastClaimedAt: string | null;
	lastError: string | null;
	isStale: boolean;
}

/** The machinery behind the store. None of it blocks checkout, which is why it needs a screen. */
export interface OperationsHealth {
	workers: WorkerHealth[];
	workersReporting: boolean;
	anyWorkerStale: boolean;
	email: {
		configured: boolean;
		pending: number;
		failed: number;
		oldestPendingAt: string | null;
		/** Money taken with no email provider configured — a persistent banner, not a metric. */
		unreceiptedSales: boolean;
	};
	events: {
		oldestUnprocessedAt: string | null;
		failed: number;
	};
	deliveries: {
		oldestPendingAt: string | null;
		pending: number;
		dead: number;
	};
}

/** Runtime readiness (§4.5) — re-evaluated continuously, not a first-run checklist. */
export interface Readiness {
	isOpen: boolean;
	unmet: UnmetCondition[];
	/** Present on `/api/admin/setup-status`, which carries the operational health alongside. */
	health?: OperationsHealth;
}

/** Per-version row counts, so a key rotation is observably complete before a key is dropped. */
export interface ReEncryptResult {
	activeKeyVersion: number;
	total: number;
	reEncrypted: number;
	failures: { uuid: string; error: string }[];
	byKeyVersion: Record<string, number>;
}

/** The sentinel a credential field submits when the operator did not touch it (§12). */
export const UNCHANGED_SECRET = 'unchanged';

/** What a credential field submits: a new value, or the sentinel above. */
export interface PaymentProviderWriteInput {
	type?: string;
	name?: string;
	mode?: PaymentProviderMode;
	credentials?: Record<string, string>;
	options?: Record<string, unknown>;
	isActive?: boolean;
	isDefault?: boolean;
}

export interface CurrencyWriteInput {
	code?: string;
	name?: string;
	symbol?: string;
	minorUnitDigits?: number;
	isEnabled?: boolean;
	isDefault?: boolean;
	displayOrder?: number;
	roundingIncrement?: number;
}

// --- Catalog (Design §4.1, §13) ---------------------------------------------------------------

/**
 * What the Buy control does for this caller, **computed server-side** from `Product.rank` and the
 * caller's subscription (§9.3).
 *
 * Absent for an anonymous caller. Deriving it in the client would put the rank rule in a second
 * place, where it will drift from the server's — and the two disagreeing means a button that
 * offers an upgrade the API then refuses.
 */
export type PriceAction = 'buy' | 'current' | 'upgrade' | 'downgrade' | 'unavailable';

/** Why an action is `unavailable`: not priced in this currency, or blocked by a pending change. */
export type PriceBlockedReason = 'currency' | 'pendingChange';

/**
 * Null = one-time. `day` exists for testing (§4.1) — every other interval takes a month or a year
 * to produce a second invoice, which makes the whole renewal half of the system unobservable in a
 * development store.
 */
export type BillingInterval = 'day' | 'month' | 'year';
export type TaxBehavior = 'inclusive' | 'exclusive';
export type ProductType = 'subscription' | 'one_time';

export interface PriceAmount {
	currency: string;
	amountMinor: number;
}

export interface Price {
	uuid: string;
	interval: BillingInterval | null;
	intervalCount: number;
	baseCurrency: string;
	taxBehavior: TaxBehavior;
	trialDays: number;
	trialRequiresPaymentMethod: boolean;
	trialEndBehavior: 'create_invoice' | 'cancel' | 'pause';
	isDefault: boolean;
	isActive: boolean;
	/** The currency this DTO was priced in — the request's `?currency=`, or the store default. */
	currency: string;
	/** Minor units in `currency`, or null when this price carries no amount in it. */
	amount: number | null;
	/** §10 as a rendered state: shown as unavailable rather than silently falling back. */
	unavailable: boolean;
	amounts: PriceAmount[];
	/**
	 * Whether `amount` is a **unit** price to be multiplied by seats (§9.7). A family may hold flat
	 * and per-seat tiers side by side, so this is per price and never per product.
	 */
	isSeatBased: boolean;
	/** The seat floor. 1 on a flat price, so no caller needs a branch to apply it. */
	seatMin: number;
	/** The seat ceiling, or null for none. */
	seatMax: number | null;
	action?: PriceAction;
	blockedReason?: PriceBlockedReason | null;
	pendingChangeEffectiveAt?: string | null;
	/**
	 * The subscription an `upgrade` or `downgrade` acts on (§9.3). Present only for those two
	 * actions; a `buy` has nothing to change.
	 */
	subscriptionUUID?: string | null;
	/**
	 * Seats currently held on this plan's group (§9.7). Present when the caller holds a
	 * subscription in it; lets a per-seat `current` price offer a seat change rather than being a
	 * dead end that says only "current plan".
	 */
	heldSeats?: number | null;
	/**
	 * Whether the price can still be edited in place (§4.1), and why not when it cannot.
	 *
	 * Admin reads only. A price leaves draft the moment a provider, a subscription, an order or an
	 * open checkout depends on it; after that an edit would reprice somebody, so the screen offers
	 * Replace instead and says which of the four pinned it.
	 */
	isDraft?: boolean;
	notDraftReason?: string | null;
}

/**
 * One row of the tier comparison table (§14).
 *
 * `label` is the key the columns align on, so two tiers describing one capability must spell it
 * identically or they render as two rows. `value` is what this tier gives: a string renders as
 * itself, **null renders as a tick**, and a label a product omits renders as a dash.
 */
export interface ProductComparisonFeature {
	group: string | null;
	label: string;
	value: string | null;
	/**
	 * Whether this row is also printed on the invoice document (§4.6).
	 *
	 * Optional, and absent is false — every row authored before this existed carries no key, and a
	 * boolean has no third state for that absence to be mistaken for. Unlike `value`, where null is
	 * the tick and so an omitted field would read as a claim nobody made.
	 */
	includeOnInvoice?: boolean;
	/**
	 * Whether this row is listed when a customer cancels. Optional, and absent is false, for the reason
	 * `includeOnInvoice` gives.
	 */
	includeOnCancel?: boolean;
}

/** What a purchase ends on: a sentence and the links to go on to. */
export interface PostPurchaseContent {
	message: string | null;
	links: { label: string; url: string }[];
}

export interface Product {
	/** What a purchase ends on — a message and links on — or null (§14). */
	postPurchase?: PostPurchaseContent | null;
	uuid: string;
	slug: string;
	familySlug: string | null;
	familyName: string | null;
	/** The legal entity that sells it (§4.6), or null for the store's default seller. */
	sellerUUID: string | null;
	sellerName: string | null;
	/**
	 * Terms the buyer must tick before this can be bought (§4.7).
	 *
	 * Present on the single-product read and absent from the list, so a client that finds none here
	 * is looking at a list rather than at a product with no terms.
	 */
	clickwraps?: Clickwrap[];
	/**
	 * Options the buyer may take on this purchase (§4.8), priced in the currency this was read in.
	 *
	 * Beside `clickwraps` and present on the same read, because the two are the same shape of thing
	 * to the storefront — words with a checkbox — and differ only in whether ticking is required.
	 */
	options?: ProductOption[];
	/** Which terms name this product (§4.7) — what the admin form prefills its checkboxes from. */
	clickwrapUUIDs?: string[];
	/** Which options are offered on this product (§4.8) — the same, for the option form. */
	optionUUIDs?: string[];
	rank: number;
	name: string;
	tagline: string | null;
	/** HTML the app did not author — render inside `.richtext`. */
	description: string | null;
	type: ProductType;
	entitlementKey: string;
	/**
	 * The short badge the storefront puts on the tier the line promotes — "Most popular" (§14).
	 *
	 * Optional rather than required, like `imageURL`: the field is newer than some deployments,
	 * and a client that demands it cannot render against a server that has not been updated.
	 */
	highlight?: string | null;
	/**
	 * What this tier gives (§14) — the one statement the comparison table, the catalogue cards and
	 * the product page are all drawn from. It replaced a separate `featureList` of marketing
	 * bullets: two fields saying what a tier gives could not agree by construction, and the
	 * storefront rendered both.
	 *
	 * Optional rather than required, like `imageURL`: the field is newer than some deployments,
	 * and a client that demands it cannot render against a server that has not been updated.
	 */
	comparisonFeatures?: ProductComparisonFeature[];
	/**
	 * Whether a one-time product can be bought again by somebody who already holds it (§4.1) — a
	 * credit pack, an extra slot, a second copy. False on every subscription, where buying again is
	 * a plan change.
	 *
	 * Optional rather than required, like `imageURL`: the field is newer than some deployments, and
	 * a client that demands it cannot render against a server that has not been updated. Absent is
	 * read as "not repeatable", which is what every product was before the field existed.
	 */
	isRepeatable?: boolean;
	/**
	 * How many seats one unit carries (§4.1) — "activates on two machines". Multiplies the units
	 * bought into `Entitlement.seats`, except on a seat-based price. Optional for the reason
	 * `isRepeatable` is; absent is 1.
	 */
	seatsPerUnit?: number;
	refundWindowDays: number | null;
	publishedAt: string | null;
	isActive: boolean;
	isPublic: boolean;
	currency: string;
	prices: Price[];
	/** Admin responses only — provider vocabulary, never the public storefront. */
	taxCodes?: Record<string, string> | null;
	/**
	 * Where the product's logo is served from, or null (§4.1).
	 *
	 * A URL rather than a uuid, because every consumer of this wants an `<img src>`. `imageUUID`
	 * sits beside it for the admin form, which writes by uuid.
	 */
	imageURL?: string | null;
	imageUUID?: string | null;
}

export interface ProductFamily {
	/** What a purchase ends on — a message and links on — or null (§14). */
	postPurchase?: PostPurchaseContent | null;
	slug: string;
	name: string;
	description: string | null;
	/**
	 * Where this line sits among the others (§14) — lower first, ties broken by name.
	 *
	 * Optional, like `imageURL`: the field is newer than some deployments, and a client that
	 * demands it cannot render against a server that has not been updated. Absent reads as zero,
	 * which is what every line carried before anyone rearranged one.
	 */
	displayOrder?: number;
	isActive: boolean;
	products: Product[];
	/** Which terms name this family (§4.7) — a family term covers every product in the line. */
	clickwrapUUIDs?: string[];
	/** Which options this line offers (§4.8) — covering every product in it, present and future. */
	optionUUIDs?: string[];
	/** Where the line's logo is served from, or null. See `Product.imageURL`. */
	imageURL?: string | null;
	imageUUID?: string | null;
}

export type CouponDuration = 'once' | 'repeating' | 'forever';

export interface Coupon {
	code: string;
	discountType: 'percent' | 'fixed';
	percentOff: number | null;
	duration: CouponDuration;
	durationInMonths: number | null;
	maxRedemptions: number | null;
	redemptionCount: number;
	expiresAt: string | null;
	appliesToProductUUIDs: string[] | null;
	/**
	 * Lines the coupon covers, every plan in them included — new ones as well, which is why a
	 * family scope is not the same as listing the line's plans today.
	 *
	 * A union with `appliesToProductUUIDs`, not an intersection. Both null means every product.
	 */
	appliesToFamilySlugs: string[] | null;
	isActive: boolean;
	amounts: PriceAmount[];
}

// --- Checkout (Design §6.2, §14) ---------------------------------------------------------------

// --- Catalog authoring inputs ------------------------------------------------------------------

export interface FamilyWriteInput {
	slug?: string;
	name?: string;
	description?: string | null;
	isActive?: boolean;
	/**
	 * Which terms apply to this family (§4.7). Sending it adds and removes scope rows for **this
	 * family only**; omitting it leaves them alone, so the list's Active toggle cannot strip them.
	 */
	clickwrapUUIDs?: string[];
	/** Which options this family offers (§4.8). Same replace-or-leave-alone rule as above. */
	optionUUIDs?: string[];
	/** Null clears the logo, which is a value; omitting the field leaves it alone. */
	imageUUID?: string | null;
	/** Null clears it; omitting leaves it alone. */
	postPurchase?: PostPurchaseContent | null;
}

/**
 * A term a buyer must tick before purchasing (§4.7).
 *
 * The `version` travels with the tick: wording that changed while the customer was reading is
 * refused rather than silently treated as agreed, which is the claim a clickwrap exists to defeat.
 */
export interface Clickwrap {
	uuid: string;
	title: string;
	/** HTML the app did not author — render inside `.richtext`. */
	bodyHTML: string;
	version: number;
	displayOrder: number;
	/**
	 * The question the buyer must answer beside the tick, or null where the term asks none (§4.7).
	 *
	 * Optional as well as nullable, because a server that predates the field sends neither — and
	 * `undefined` is not null to anything that tests it. Present means the answer is **required**;
	 * the server refuses the purchase without one, so the storefront gates on it too rather than
	 * letting the customer find out at the payment page.
	 */
	inputLabel?: string | null;
}

export interface AdminClickwrap extends Clickwrap {
	isActive: boolean;
	/** Every product it applies to. A term is written once and used wherever it is needed. */
	productUUIDs: string[];
	/** Every family it applies to; a family covers every product in the line. */
	familySlugs: string[];
	/** How many customers have accepted it, which makes retiring it a considered act. */
	acceptanceCount: number;
}

export interface ClickwrapWriteInput {
	title?: string;
	bodyHTML?: string;
	displayOrder?: number;
	isActive?: boolean;
	/**
	 * The question this term asks, or null to stop asking one.
	 *
	 * Null and omitted are different instructions: null clears it, omitting leaves it alone. Adding
	 * one bumps the version, because a page already on screen has no field to answer it with.
	 */
	inputLabel?: string | null;
	/**
	 * Sending either list **replaces** the scope. Sending one and not the other means "these
	 * products, no families" — a partial merge would make removing every family inexpressible.
	 */
	productUUIDs?: string[];
	familySlugs?: string[];
}

/**
 * What a delete actually did.
 *
 * A term nobody accepted is a draft and goes; one somebody accepted is evidence and is retired
 * instead. Either way it comes off every product and family, which is what Delete is asking for.
 */
export interface ClickwrapRemoval {
	uuid: string;
	deleted: boolean;
	acceptanceCount: number;
	scopesRemoved: number;
}

// --- Product options (§4.8) --------------------------------------------------------------------

/**
 * How an option changes the price, and null for one that does not.
 *
 * Named for the **adjustment** rather than the discount, because the value is signed: an option
 * can take money off *or* put it on.
 */
export type ProductOptionAdjustmentType = 'percent' | 'fixed';

/**
 * An option a buyer **may** take at purchase (§4.8) — a clickwrap's sibling, and the difference is
 * one word: a term is required and an option is offered.
 *
 * Two things can hang off taking one, and a store may use either or both: a **discount**, and a
 * **key** the application reads out of `entitlement.options` to turn a feature on. An option with
 * no discount is a pure feature toggle and is entirely ordinary.
 *
 * The `version` travels with the tick for the reason a term's does, plus one of its own: it covers
 * the discount, so a stale one is a buyer charged against a figure they were never quoted.
 */
export interface ProductOption {
	uuid: string;
	/** The string the application switches on. Frozen once anybody has taken the option. */
	key: string;
	title: string;
	/** HTML the app did not author — render inside `.richtext`. */
	bodyHTML: string;
	version: number;
	displayOrder: number;
	adjustmentType: ProductOptionAdjustmentType | null;
	/** Signed: **negative takes money off, positive puts it on**. Null unless `percent`. */
	percentAdjustment: number | null;
	/**
	 * Signed minor units, for a **fixed** option in the currency this product was read in. Null on
	 * a percentage option and on one that changes no money — render it through `PriceDisplay` like
	 * every other money figure, never by dividing by 100.
	 */
	amount: number | null;
	/** Whether the money moves once rather than every period — a setup fee against a standing one. */
	chargeOnce: boolean;
	/**
	 * True where the option is a fixed discount the store holds no amount for in this currency.
	 *
	 * Present and flagged rather than omitted, exactly as an unavailable price is: an offer that
	 * silently disappears when the customer switches currency reads as a store that changed its
	 * mind, and support has nothing to look at.
	 */
	unavailable: boolean;
}

export interface AdminProductOption extends Omit<ProductOption, 'amount' | 'unavailable'> {
	/** Signed minor units per currency, keyed by lowercase ISO-4217 code. Empty unless `fixed`. */
	amounts: Record<string, number>;
	isActive: boolean;
	/** Every product it is offered on. An offer is written once and used wherever it applies. */
	productUUIDs: string[];
	/** Every family it is offered on; a family covers every product in the line. */
	familySlugs: string[];
	/** How many purchases took it, which makes retiring it a considered act. */
	selectionCount: number;
	/**
	 * Whether the key can still be changed — false once anybody has taken the option.
	 *
	 * The key is copied onto every selection and shipped in every entitlement payload, so changing
	 * it would strand existing holders on the old string while new buyers get the new one.
	 */
	keyIsEditable: boolean;
}

export interface ProductOptionWriteInput {
	key?: string;
	title?: string;
	bodyHTML?: string;
	displayOrder?: number;
	isActive?: boolean;
	/**
	 * Null discounts nothing, which is a whole state rather than a missing one: an option can exist
	 * purely to turn a feature on. Null and omitted are different instructions, as everywhere else.
	 */
	adjustmentType?: ProductOptionAdjustmentType | null;
	/** Signed — negative off, positive on. Never zero, which adjusts nothing. */
	percentAdjustment?: number | null;
	/** Signed minor units per currency, keyed by lowercase ISO-4217 code. */
	amounts?: Record<string, number>;
	chargeOnce?: boolean;
	/** Sending either list **replaces** the scope, exactly as a term's does. */
	productUUIDs?: string[];
	familySlugs?: string[];
}

/**
 * What a delete actually did.
 *
 * An option nobody took is a draft and goes; one somebody took is the record of a discount the
 * store gave and is retired instead. Either way it comes off every product and family.
 */
export interface ProductOptionRemoval {
	uuid: string;
	deleted: boolean;
	selectionCount: number;
	scopesRemoved: number;
}

/**
 * One option a customer took on one purchase, as it is read back (§4.8).
 *
 * Everything here is the **snapshot taken at the moment of the purchase**, not the live option —
 * and it answers two questions rather than one: what was the customer charged, and what may the
 * application do. The discount is editable, so quoting today's percentage beside last year's date
 * would restate a purchase as having been sold on terms nobody offered then.
 */
export interface ProductOptionSelection {
	uuid: string;
	/** The option it took, for finding it on the catalogue screen. Null once the option is gone. */
	optionUUID: string | null;
	/** The application-facing key as it was then. */
	key: string;
	title: string;
	/** HTML the app did not author — render inside `.richtext`. */
	bodyHTML: string;
	version: number;
	/**
	 * What it took **off**, and null where it took nothing off — which includes every option that
	 * charged instead. The charge fields below are the other half; one selection fills one set,
	 * because the two are the two objects the money actually moved through at the provider.
	 */
	discountType: ProductOptionAdjustmentType | null;
	percentOff: number | null;
	/** What it actually took off, in minor units of `currency`. Null where it discounted nothing. */
	discountAmount: number | null;
	/** What it **added**, in minor units of `currency`. Null where it added nothing. */
	chargeAmount: number | null;
	/** The percentage that produced the charge, where it was one. */
	chargePercent: number | null;
	/** Whether the money moved once rather than every period. */
	chargeOnce: boolean;
	currency: string | null;
	/** Whether the live option still reads this way. Null where the option no longer exists. */
	isCurrentVersion: boolean | null;
	selectedAt: string;
}

/** An uploaded catalogue image (§4.1). */
export interface MediaAsset {
	uuid: string;
	fileName: string;
	contentType: string;
	byteSize: number;
	/** Where it is served from — relative, so it works behind whatever host the store runs on. */
	url: string;
}

/**
 * One subscription on the store-wide admin list, with the customer it belongs to (§13).
 *
 * It carries the customer's identity because the point of a store-wide list is that the reader
 * does not already know whose row this is: a list of subscriptions with no names on it answers
 * "how many are past due" and nothing else, and the next question is always "whose".
 */
export interface AdminSaleSubscription {
	uuid: string;
	status: SubscriptionStatus;
	currency: string;
	/** `amount × quantity` — what is actually billed each period, never the per-seat unit (§9.7). */
	total: number | null;
	quantity: number;
	interval: BillingInterval | null;
	intervalCount: number;
	currentPeriodEnd: string | null;
	trialEnd: string | null;
	cancelAtPeriodEnd: boolean;
	createdAt: string;
	productName: string;
	productSlug: string;
	/** Addressed by the **user's** uuid: one customer is one user, so it has no id of its own. */
	userUUID: string;
	customerName: string | null;
	customerEmail: string | null;
}

/** One one-time purchase on the store-wide admin list, with the customer it belongs to. */
export interface AdminSaleOrder {
	uuid: string;
	status: AccountOrder['status'];
	currency: string;
	total: number | null;
	paidAt: string | null;
	createdAt: string;
	productName: string;
	productSlug: string;
	/** Null while the order is still awaiting its invoice — there is nothing to refund against. */
	invoiceUUID: string | null;
	userUUID: string;
	customerName: string | null;
	customerEmail: string | null;
}

export interface ProductWriteInput {
	/** Null clears it; omitting leaves it alone. */
	postPurchase?: PostPurchaseContent | null;
	slug?: string;
	familySlug?: string | null;
	/** §4.6. Null means the store's default seller — a value, not an absence. */
	sellerUUID?: string | null;
	/**
	 * Which terms apply to this product (§4.7).
	 *
	 * Sending it adds and removes scope rows for **this product only** — a term covering four plans
	 * does not lose the other three because somebody edited the fourth. Omitting it leaves them.
	 */
	clickwrapUUIDs?: string[];
	/**
	 * Which options are offered on this product (§4.8). Exact for this product, as above.
	 */
	optionUUIDs?: string[];
	/**
	 * Which applications may read this product's entitlement key (§4.3), by AbstractID app uuid.
	 *
	 * The same fact as `Application.subscribedEntitlementKeys`, written from the product's side.
	 * A present list is **exact for this key** and leaves every other key each application holds
	 * alone; omitting it leaves every scope untouched. Applications set to `allKeys` are never in
	 * it — the flag grants them the key, not the list.
	 */
	applicationUUIDs?: string[];
	rank?: number;
	name?: string;
	tagline?: string | null;
	description?: string | null;
	type?: ProductType;
	/** Write-once past `publishedAt`: the server refuses a change, and the form mirrors it. */
	entitlementKey?: string;
	/** The promoted-tier badge (§14). Null clears it; omitting the field leaves it alone. */
	highlight?: string | null;
	/** What the tier gives (§14). Null clears the rows; omitting the field leaves them alone. */
	comparisonFeatures?: ProductComparisonFeature[] | null;
	taxCodes?: Record<string, string> | null;
	/** §4.1 — one-time products only; the server refuses it on a subscription by name. */
	isRepeatable?: boolean;
	/** §4.1 — refused by name once anybody holds the product. Omit it to leave it alone. */
	seatsPerUnit?: number;
	/** Null clears the logo, which is a value; omitting the field leaves it alone. */
	imageUUID?: string | null;
	refundWindowDays?: number | null;
	isActive?: boolean;
	isPublic?: boolean;
}

export interface PriceWriteInput {
	productUUID: string;
	interval?: BillingInterval | null;
	intervalCount?: number;
	baseCurrency: string;
	taxBehavior?: TaxBehavior;
	trialDays?: number;
	trialRequiresPaymentMethod?: boolean;
	trialEndBehavior?: 'create_invoice' | 'cancel' | 'pause';
	isDefault?: boolean;
	/**
	 * Whether the amounts below are the price of **one seat** (§9.7). Seat terms are refused by
	 * name on a flat price, so they are sent only when this is true.
	 */
	isSeatBased?: boolean;
	seatMin?: number;
	/** Null means no ceiling — never zero, which would be a plan nobody can hold. */
	seatMax?: number | null;
	amounts: PriceAmount[];
}

/** Only the two fields that carry no repricing risk. Everything else needs a new price (§4.1). */
export interface PriceUpdateInput {
	isDefault?: boolean;
	isActive?: boolean;
}

export interface CouponWriteInput {
	code?: string;
	discountType?: 'percent' | 'fixed';
	percentOff?: number | null;
	duration?: CouponDuration;
	durationInMonths?: number | null;
	maxRedemptions?: number | null;
	expiresAt?: string | null;
	appliesToProductUUIDs?: string[] | null;
	appliesToFamilySlugs?: string[] | null;
	isActive?: boolean;
	amounts?: PriceAmount[];
}

// --- Applications (Design §4.3, §8.3) ---------------------------------------------------------

/**
 * A consuming application, as admin reads it.
 *
 * **No read ever carries the API key, its hash or a signing secret** (Design §12): the key is
 * shown once at creation or rotation and never again, and `apiKeyPrefix` is the only part of it
 * any read endpoint returns.
 */
export interface StoreApplication {
	abstractIDAppUUID: string;
	name: string;
	webhookURL: string | null;
	apiKeyPrefix: string | null;
	apiKeyLastUsedAt: string | null;
	apiKeyRotatedAt: string | null;
	previousAPIKeyExpiresAt: string | null;
	previousSecretExpiresAt: string | null;
	allowedIPs: string[] | null;
	/** **Null means no keys**, never all keys — it is an authorization scope (Design §8.3). */
	subscribedEntitlementKeys: string[] | null;
	/** The explicit, audited opt-in for a global consumer. */
	allKeys: boolean;
	rateLimitPerMinute: number | null;
	subjectsPerMinute: number | null;
	isActive: boolean;
	hasSigningSecret: boolean;
	creationDate: string;
}

export interface ApplicationWriteInput {
	abstractIDAppUUID?: string;
	name?: string;
	webhookURL?: string | null;
	allowedIPs?: string[] | null;
	subscribedEntitlementKeys?: string[] | null;
	allKeys?: boolean;
	rateLimitPerMinute?: number | null;
	subjectsPerMinute?: number | null;
	isActive?: boolean;
}

/** The one response that carries a secret. Shown once; there is no endpoint that will repeat it. */
/**
 * One row of the outbound outbox (Design §4.3, §8.1), as the admin table renders it.
 *
 * Two fields on it carry more meaning than their names suggest. `status` distinguishes a row in
 * backoff (`failed`) from one that has given up (`dead`) — and **dead is not the end**: the store
 * enqueues a repair alongside it, which is the `isResync` row. So a table showing only dead rows
 * reads as "these customers are broken" when the truthful reading is "these are being repaired".
 */
export interface WebhookDelivery {
	uuid: string;
	applicationUUID: string;
	applicationName: string;
	eventType: string;
	userUUID: string | null;
	entitlementKey: string | null;
	version: number | null;
	status: 'pending' | 'delivering' | 'delivered' | 'failed' | 'dead';
	/** The repair enqueued by a dead letter: slow, indefinite, and never dead-lettered itself. */
	isResync: boolean;
	attemptCount: number;
	nextAttemptAt: string | null;
	lastAttemptAt: string | null;
	lastResponseStatus: number | null;
	lastError: string | null;
	/**
	 * What the receiver actually answered, on success as well as failure (§8.1).
	 *
	 * **Null means no answer was seen** — a timeout, a refused connection, a URL the SSRF check
	 * turned down — which is a different thing from an answer that was empty, and the screen says
	 * so rather than rendering both as blank.
	 */
	lastResponseBody: string | null;
	/** What they actually sent, so a truncated body reads as truncated rather than as brief. */
	lastResponseBytes: number | null;
	/** Redacted server-side: no `set-cookie` or `authorization` ever reaches this screen. */
	lastResponseHeaders: Record<string, string> | null;
	/** Wall-clock milliseconds for the last attempt — the number behind "the receiver is slow". */
	lastDurationMs: number | null;
	deliveredAt: string | null;
	creationDate: string;
}

export interface WebhookDeliveryFilters {
	applicationID?: string;
	status?: string;
	since?: string;
	limit?: number;
	offset?: number;
}

export interface IssuedApplicationCredential {
	application: StoreApplication;
	apiKey?: string;
	signingSecret?: string;
	previousExpiresAt?: string | null;
}

/**
 * One object's outcome from a catalog sync (Design §5.3). Reported per object rather than as a
 * single pass/fail, because a partial failure — six products pushed and the seventh rejected for
 * a bad tax code — otherwise renders as "sync ran" while the storefront quietly cannot sell one
 * product on that provider.
 */
export interface SyncObjectResult {
	objectType: 'product' | 'price' | 'coupon' | 'customer';
	identifier: string;
	name: string;
	status: 'synced' | 'failed';
	providerObjectID: string | null;
	error: string | null;
}

export interface CatalogSyncResult {
	providerUUID: string;
	providerName: string;
	total: number;
	synced: number;
	failed: number;
	results: SyncObjectResult[];
}

// --- Account (Design §9, §13) ------------------------------------------------------------------

/**
 * Every status the provider can put a subscription in (§9.1).
 *
 * `unpaid` and `paused` are in the enum because a provider produces them, not because the design
 * wants them: which state retries exhaust into is an account-level setting, and a client that
 * only knows the four states of the happy path renders them as nothing at all.
 */
export type SubscriptionStatus =
	| 'incomplete'
	| 'trialing'
	| 'active'
	| 'past_due'
	| 'unpaid'
	| 'paused'
	| 'canceled'
	| 'expired';

export interface AccountEntitlement {
	entitlementKey: string;
	productSlug: string | null;
	productName: string | null;
	/**
	 * The family the product belongs to, so a set of tiers presents as **one** thing the customer
	 * has rather than one row per key. Null for a standalone product, which is its own group.
	 */
	familySlug: string | null;
	familyName: string | null;
	/** Which tier within the family; the group presents the highest one still held. */
	productRank: number | null;
	/** `grace` is still access — a failed renewal, not a lost one (§9.5). */
	status: 'active' | 'grace' | 'expired';
	source: 'subscription' | 'one_time' | 'manual';
	expiresAt: string | null;
	trialEnd: string | null;
	graceUntil: string | null;
}

/**
 * A downgrade that has been scheduled at the provider and has not fired yet (§9.3).
 *
 * A **mirror** of the provider's schedule rather than the authority: the switch is atomic with
 * the cycle roll, and this exists so the account area can say what is coming and offer to undo it.
 */
export interface PendingSubscriptionChange {
	priceUUID: string;
	productUUID: string;
	productName: string;
	effectiveAt: string;
	amount: number | null;
	currency: string;
	interval: BillingInterval | null;
	intervalCount: number;
}

export interface AccountSubscription {
	uuid: string;
	status: SubscriptionStatus;
	productUUID: string;
	productSlug: string;
	productName: string;
	priceUUID: string;
	interval: BillingInterval | null;
	intervalCount: number;
	/** The **unit** amount on a per-seat plan; the amount outright on a flat one. */
	amount: number | null;
	/** Whether `amount` is per seat, so a screen knows which of the two figures to lead with. */
	isSeatBased: boolean;
	seatMin: number;
	seatMax: number | null;
	/** `amount × quantity` — what is actually billed each period. Never stored, always derived. */
	total: number | null;
	currency: string;
	/** Seats held. 1 on a flat plan. */
	quantity: number;
	currentPeriodStart: string | null;
	currentPeriodEnd: string | null;
	trialEnd: string | null;
	/** Will not renew: the provider cancelling at period end, or a scheduled end the store holds. */
	cancelAtPeriodEnd: boolean;
	/**
	 * When access ends, where it is going to — read this, never `currentPeriodEnd`, which on a term
	 * billed monthly is this month's end rather than the term's. Optional because a
	 * server older than the field sends none.
	 */
	endsAt?: string | null;
	canceledAt: string | null;
	/** When grace runs out (§7.1). Shipped in the payload so the UI can name the deadline. */
	graceUntil: string | null;
	pendingChange: PendingSubscriptionChange | null;
	coupon: {
		code: string;
		discountType: 'percent' | 'fixed';
		percentOff: number | null;
		duration: CouponDuration;
		durationInMonths: number | null;
	} | null;
	createdAt: string;
	/**
	 * §4.7 — what this purchase was agreed to, and what the customer typed into it.
	 *
	 * Present on the single-subscription read and absent from the list, so a client that finds none
	 * here is looking at a list rather than at a purchase that carried no terms — which is why it
	 * is optional rather than defaulted to an empty array. `AcceptedTerms` says the two differently.
	 */
	acceptedTerms?: ClickwrapAcceptance[];
	/**
	 * §4.8 — the options this purchase was made with, and what each of them took off.
	 *
	 * Beside the terms, present on the single read and absent from the list for the same reason:
	 * both are a join per subscription, and the list renders a page of them.
	 */
	selectedOptions?: ProductOptionSelection[];
}

/**
 * What a plan change costs, in the two shapes it can take (§9.3).
 *
 * Discriminated because a downgrade takes no payment, and an "amount to be charged" shape has
 * nothing to say about it — while the downgrade confirm step is exactly where a customer most
 * needs "you keep Studio until 14 Sep, then €9/month". Rendering only `chargeNow` would leave
 * that step blank or, worse, quoting a figure nobody is charged.
 */
export type ProrationPreview =
	| {
			kind: 'immediate';
			chargeNow: number;
			credit: number;
			tax: number;
			total: number;
			/**
			 * What the ordinary invoice will say once the proration is behind them, and when.
			 *
			 * A proration total is a one-off arrived at from credits and part-periods, so it is
			 * almost never what the customer pays each period afterwards — on an upgrade it is
			 * usually far smaller, because the credit for the tier being left eats most of it.
			 * Shown alone it reads as the new price of the plan, which is the number the customer
			 * remembers and the one they dispute when the first full invoice arrives.
			 */
			nextInvoiceAmount: number | null;
			nextInvoiceAt: string | null;
			currency: string;
		}
	| { kind: 'scheduled'; effectiveAt: string; nextInvoiceAmount: number; currency: string };

/**
 * §9.6's eligibility, carried inline on every invoice and order.
 *
 * `message` is customer-facing copy and is rendered as-is when `eligible` is false: the server owns
 * the wording so the refusal the list shows and the one the confirm step gives cannot disagree.
 * `null` on a row with nothing to evaluate — an order whose invoice has not been raised yet.
 */
export interface RefundEligibility {
	eligible: boolean;
	reasonCode:
		| 'ok'
		| 'window_closed'
		| 'not_first_invoice'
		/**
		 * The payment is for a plan the customer has since changed off (§9.6).
		 *
		 * The opposite complaint to `not_first_invoice`, which is why it is its own code: that one
		 * refuses an invoice for being *late* in the subscription, this one for being *early*.
		 */
		| 'superseded'
		| 'already_refunded'
		/**
		 * The charge behind this payment was charged back through the bank (§9.6).
		 *
		 * Its own code rather than `already_refunded`: money taken back by force and money given
		 * back are opposite events, and the provider refuses a refund on top of a dispute outright.
		 */
		| 'charged_back'
		| 'annual_limit'
		| 'product_disabled'
		| 'not_owner';
	message: string;
	refundableAmount: number | null;
	currency: string | null;
	windowEndsAt: string | null;
}

/** What `POST /api/account/refund` and the admin refund answer with. */
export interface IssuedRefund {
	uuid: string;
	amount: number;
	currency: string;
	status: 'pending' | 'succeeded' | 'failed' | 'canceled';
	note: string | null;
	revokedEntitlement?: boolean;
	providerRefundID?: string | null;
}

/** A refund as the admin customer page lists it. */
export interface StoreRefund {
	uuid: string;
	amount: number;
	currency: string;
	reason: 'customer_self_serve' | 'admin' | 'chargeback';
	status: 'pending' | 'succeeded' | 'failed' | 'canceled';
	note: string | null;
	revokedEntitlement: boolean;
	providerRefundID: string | null;
	invoiceUUID: string | null;
	orderUUID: string | null;
	subscriptionUUID: string | null;
	initiatedByUserID: number | null;
	createdAt: string;
	/**
	 * The credit note documenting this refund (§4.6), where the Store issued one.
	 *
	 * Null is **two different things**, and the screen says which: a store letting the payment
	 * provider issue its own documents never mints one, and a refund whose issue failed has one
	 * outstanding. The admin row offers an issue button on the second — an absent legal document
	 * nobody can see is one nobody reissues.
	 */
	creditNote: RefundCreditNote | null;
}

/** Enough of a credit note to name it and open its PDF. `total` is negative. */
export interface RefundCreditNote {
	uuid: string;
	documentNumber: string;
	issuedAt: string;
	total: number;
	currency: string;
}

/**
 * One row of the admin customer list. Addressed by the **user's** uuid: a customer is one row per
 * user, so it has no external identifier of its own.
 */
export interface AdminCustomer {
	userUUID: string;
	userID: number;
	displayName: string | null;
	email: string | null;
	billingName: string | null;
	billingCompany: string | null;
	billingEmail: string | null;
	currency: string | null;
	balance: number;
	isFlagged: boolean;
	flaggedReason: string | null;
	subscriptionCount: number;
	orderCount: number;
	/** When the **account** appeared. `isCustomer` says whether it has ever bought anything. */
	createdAt: string;
	/** False for an account with no customer row: signed in, never started a checkout. */
	isCustomer: boolean;
	isAdmin: boolean;
	lastLogin: string | null;
}

export interface AdminInvoice {
	uuid: string;
	/** The **provider's** number. Ours is `documentNumber`; the two are not interchangeable (§4.6). */
	number: string | null;
	/**
	 * Our own invoice number, where the Store issued the document — the reference printed on the
	 * PDF the customer is holding. Render through `invoiceReference`, never `number` alone.
	 */
	documentNumber: string | null;
	status: string;
	currency: string;
	total: number | null;
	amountPaid: number | null;
	/** Minor units already refunded against it (§9.6). Render through `invoiceStatusLabel`. */
	refundedAmount: number;
	issuedAt: string | null;
	paidAt: string | null;
	/**
	 * The provider's own copy — **null once the Store has issued its own document** (§4.6).
	 *
	 * Withheld server-side, so every screen gets the same answer. Support needs the link at all
	 * because every admin table that listed an invoice listed it with no way to open it, and "can
	 * you send me that invoice again" then ended at a column of numbers.
	 */
	hostedInvoiceURL: string | null;
	invoicePDFURL: string | null;
	/** The credit notes issued against it (§4.6), oldest first. Empty rather than null. */
	creditNotes: InvoiceCreditNote[];
	productName: string | null;
	subscriptionUUID: string | null;
	orderUUID: string | null;
}

export interface AdminCustomerDetail {
	customer: AdminCustomer;
	subscriptions: AccountSubscription[];
	invoices: AdminInvoice[];
	refunds: StoreRefund[];
	entitlements: AccountEntitlement[];
	/**
	 * §4.7 — everything this customer has ever agreed to, with the purchase each was given for.
	 *
	 * Optional because a server that predates it sends nothing, and "no terms accepted" is a claim
	 * the screen must not make on an absent field: the panel says which of the two it is holding.
	 */
	acceptedTerms?: CustomerAcceptance[];
}

/**
 * One subscription, seen from the admin side (§13).
 *
 * Assembled server-side in one read rather than fetched panel by panel: a support screen that
 * issues a request per panel renders in pieces, and the pieces can disagree — an invoice list from
 * before a refund landed beside a refund list from after it.
 *
 * `invoices` is narrowed to **this subscription**; `refunds` is the customer's, unfiltered,
 * because "have they already been refunded, and for how much" is asked about the person rather
 * than the row.
 */
export interface AdminSubscriptionDetail {
	subscription: AccountSubscription;
	customer: AdminCustomer;
	invoices: AdminInvoice[];
	refunds: StoreRefund[];
	entitlements: AccountEntitlement[];
	/** §4.7 — what was agreed to for this purchase, and when. Absent on an older server. */
	acceptedTerms?: ClickwrapAcceptance[];
	/** §4.8 — the options taken on it, and what each took off. Absent on an older server. */
	selectedOptions?: ProductOptionSelection[];
}

/** One one-time purchase, seen from the admin side. Same shape, same reasoning. */
export interface AdminOrderDetail {
	order: AdminSaleOrder;
	customer: AdminCustomer;
	invoices: AdminInvoice[];
	refunds: StoreRefund[];
	entitlements: AccountEntitlement[];
	/** §4.7 — what was agreed to for this purchase, and when. Absent on an older server. */
	acceptedTerms?: ClickwrapAcceptance[];
	/** §4.8 — the options taken on it, and what each took off. Absent on an older server. */
	selectedOptions?: ProductOptionSelection[];
}

/**
 * One term a customer ticked before paying, as an admin reads it back (§4.7).
 *
 * `title` and `bodyHTML` are the **snapshot taken at the moment of the tick**, not the live term.
 * That is the whole point of the record: the words are editable, so quoting today's wording beside
 * last year's date would be evidence of something nobody agreed to — and indistinguishable from
 * evidence of something they did.
 */
export interface ClickwrapAcceptance {
	uuid: string;
	/** The term it accepted, for finding it on the catalogue screen. Null once the term is gone. */
	clickwrapUUID: string | null;
	title: string;
	/** HTML the app did not author — render inside `.richtext`. */
	bodyHTML: string;
	version: number;
	/** The question as it was asked, snapshotted. Null where the term asked none. */
	inputLabel?: string | null;
	/** What the customer typed. Null exactly when `inputLabel` is — never one without the other. */
	inputValue?: string | null;
	/** Whether the live term still reads this way. Null where the term no longer exists. */
	isCurrentVersion: boolean | null;
	acceptedAt: string;
}

/**
 * One acceptance, plus **which purchase it was given for** (§4.7).
 *
 * The customer-wide support list reads these. An acceptance only means anything against the thing
 * that was bought — the same term accepted twice is two different pieces of evidence — so the
 * purchase travels on the row and the screen groups by it, exactly as the catalogue groups products
 * by line.
 *
 * All three purchase fields are null together where the checkout session is gone or was never
 * linked. That is a real state rather than a fault, and it gets its own group instead of being
 * dropped: evidence that exists and is not shown is worse than evidence shown without its context.
 */
export interface CustomerAcceptance extends ClickwrapAcceptance {
	subscriptionUUID: string | null;
	orderUUID: string | null;
	productName: string | null;
	/** When the purchase happened, so groups read newest first rather than in row order. */
	purchasedAt: string | null;
}

/**
 * `amount` is **minor units**, and `revokeEntitlement` defaults per the full/partial rule (§9.6).
 *
 * The target is the invoice **or** the one-time purchase, and exactly one: a purchase whose
 * provider raised no invoice has no invoice to name, and refusing to refund it left money that was
 * perfectly refundable at the provider unreachable from every screen.
 */
export interface AdminRefundInput {
	invoiceUUID?: string;
	orderUUID?: string;
	amount?: number | null;
	note: string;
	revokeEntitlement?: boolean;
}

/** Enough of a credit note to name it on the invoice it credits and open its PDF (§4.6). */
export interface InvoiceCreditNote {
	uuid: string;
	documentNumber: string;
	/** Negative, as the document states it. */
	total: number;
	issuedAt: string;
}

export interface AccountInvoice {
	/** What paid it — brand and last four only — or null where the provider never said (§4.6). */
	paymentMethod?: { type: string; brand: string | null; last4: string | null } | null;
	uuid: string;
	number: string | null;
	status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
	currency: string;
	subtotal: number | null;
	tax: number | null;
	total: number | null;
	amountPaid: number | null;
	amountRemaining: number | null;
	periodStart: string | null;
	periodEnd: string | null;
	issuedAt: string | null;
	paidAt: string | null;
	/**
	 * The payment provider's own copy — **null once the Store has issued its own document** (§4.6).
	 *
	 * Withheld server-side rather than hidden here, so it holds for every screen. One charge with
	 * two documents is worse than either alone: they carry different numbers, and a customer who
	 * files the provider's copy has filed something the store's books do not know about.
	 */
	hostedInvoiceURL: string | null;
	invoicePDFURL: string | null;
	/**
	 * Our own invoice number (§4.6), present only where the Store issued the document.
	 *
	 * Which link to offer is decided from this, never from a store-wide setting: an invoice issued
	 * while the payment provider was producing the document keeps pointing at the provider's PDF
	 * forever, because that is the document the customer actually received.
	 */
	documentNumber: string | null;
	/**
	 * The credit notes issued against this invoice (§4.6), oldest first.
	 *
	 * Always an array — empty where nothing was refunded — so no screen has to tell "none" from
	 * "not loaded". Several, because several partial refunds each get their own document.
	 */
	creditNotes: InvoiceCreditNote[];
	subscriptionUUID: string | null;
	orderUUID: string | null;
	productName: string | null;
	/**
	 * Minor units already refunded against this invoice, in `currency` (§9.6).
	 *
	 * The provider's `status` cannot say it — a refund moves the *charge*, so the invoice stays
	 * `paid` forever — which is why every screen went on saying "Paid" about money the customer had
	 * already had back. Render through `invoiceStatusLabel`, never `status` alone.
	 */
	refundedAmount: number;
	refund: RefundEligibility | null;
}

export interface AccountOrder {
	uuid: string;
	status: 'pending' | 'paid' | 'refunded' | 'partially_refunded' | 'failed';
	currency: string;
	subtotal: number | null;
	discount: number | null;
	tax: number | null;
	total: number | null;
	paidAt: string | null;
	/** Units bought (§4.1) — 1 for everything not sold by the unit. */
	quantity?: number;
	productSlug: string;
	productName: string;
	createdAt: string;
	invoiceUUID: string | null;
	refund: RefundEligibility | null;
}

// -------------------------------------------------------------------------------------------
// Email (Design §11, §13) — the sender, the copy and the queue
// -------------------------------------------------------------------------------------------

export type EmailProviderType = 'smtp' | 'resend' | 'ses' | 'postmark';

/**
 * No `credentials` field, by construction: no endpoint ever returns a decrypted credential, and
 * `credentialMasks` is what the generated form renders instead (§12).
 */
export interface EmailProviderConfig {
	uuid: string;
	type: EmailProviderType;
	name: string;
	fromAddress: string | null;
	fromName: string | null;
	replyToAddress: string | null;
	isActive: boolean;
	isDefault: boolean;
	credentialMasks: Record<string, string>;
	healthStatus: 'unknown' | 'healthy' | 'unhealthy' | 'undecryptable';
	healthError: string | null;
	healthCheckedAt: string | null;
	creationDate: string;
	updatedAt: string;
}

export interface EmailProviderWriteInput {
	type?: EmailProviderType;
	name?: string;
	credentials?: Record<string, string>;
	fromAddress?: string;
	fromName?: string | null;
	replyToAddress?: string | null;
	isActive?: boolean;
	isDefault?: boolean;
}

export interface EmailTestResult {
	delivered: boolean;
	message: string;
	to: string;
}

/**
 * One template, as it will actually render.
 *
 * `isOverridden` is the whole point of the editor: a template ships as a code default and the
 * table holds overrides only, so a template can never be *missing*, only customised — and Reset
 * to default is a delete of the override rather than a copy of today's wording into the row.
 */
export interface EmailTemplateSummary {
	key: string;
	locale: string;
	subject: string;
	bodyHTML: string;
	bodyText: string;
	isOverridden: boolean;
	isActive: boolean;
	/** The placeholders the caller populates, derived from the code default rather than listed. */
	variables: string[];
	updatedAt: string | null;
	updatedByUserID: number | null;
	/** How long a queued message of this class stays truthful. Not editable — it is a class, not copy. */
	expiresAfterMs: number | null;
}

export interface EmailTemplatePreview {
	key: string;
	locale: string;
	subject: string;
	bodyHTML: string;
	bodyText: string;
	sampleData: Record<string, string>;
}

export interface EmailTemplateWriteInput {
	subject: string;
	bodyHTML: string;
	bodyText: string;
	isActive?: boolean;
}

export type EmailMessageStatus = 'pending' | 'sent' | 'failed' | 'expired';

export interface EmailMessage {
	uuid: string;
	to: string;
	templateKey: string;
	locale: string | null;
	subject: string;
	status: EmailMessageStatus;
	attemptCount: number;
	expiresAt: string | null;
	nextAttemptAt: string | null;
	sentAt: string | null;
	lastError: string | null;
	creationDate: string;
}

/** One message with its bodies, exactly as rendered at enqueue. */
export interface EmailMessageDetail extends EmailMessage {
	bodyHTML: string;
	bodyText: string;
	attachments: { kind: string; invoiceUUID: string }[];
}

// -------------------------------------------------------------------------------------------
// Operations (Design §13, §16)
// -------------------------------------------------------------------------------------------

/** One currency's slice of a money figure. Never collapsed into a scalar (§4, §10). */
export interface CurrencyAmount {
	currency: string;
	amount: number;
}

export interface CurrencyCount {
	currency: string;
	count: number;
}

export interface RevenueByProduct {
	productUUID: string;
	productName: string;
	currency: string;
	amount: number;
	orders: number;
}

export interface RevenuePeriod {
	period: string;
	currency: string;
	amount: number;
	invoices: number;
}

export interface StoreMetrics {
	mrr: CurrencyAmount[];
	activeSubscriptions: number;
	trialingSubscriptions: number;
	pastDueSubscriptions: number;
	canceledLast30Days: number;
	/** A fraction, not a percentage: 0.05 is five per cent. */
	churnRate: number;
	failedPayments: CurrencyCount[];
	revenueLast30Days: CurrencyAmount[];
	refundedLast30Days: CurrencyAmount[];
	refundRate: { currency: string; rate: number }[];
	/** Counted apart from refunds: money taken back, not money given back (§9.6). */
	chargebacksLast30Days: CurrencyAmount[];
	revenueByProduct: RevenueByProduct[];
	revenueByPeriod: RevenuePeriod[];
	payingCustomers: number;
}

/** Deliberately carries no `payload` — that is securityAdmin-only and audited per view (§12). */
export interface ProviderEvent {
	uuid: string;
	paymentProviderID: number;
	providerEventID: string;
	type: string;
	partitionKey: string | null;
	occurredAt: string;
	status: 'received' | 'processing' | 'processed' | 'failed' | 'ignored';
	attemptCount: number;
	nextAttemptAt: string | null;
	processedAt: string | null;
	error: string | null;
	hasPayload: boolean;
	creationDate: string;
}

export interface ProviderEventFilters {
	status?: string;
	type?: string;
	since?: string;
	limit?: number;
	offset?: number;
	/** So the shared query-string builder can walk it without a cast. */
	[key: string]: string | number | undefined;
}

/**
 * §16 step 8's counts.
 *
 * `driftCorrected` and `providerActiveWithoutLocalRow` are the two directions of the same signal,
 * and the second is the one no other mechanism can see: a subscription the provider is still
 * charging for that this store holds no live row for.
 */
export interface ReconciliationReport {
	driftCorrected: number;
	providerActiveWithoutLocalRow: number;
	providerSubscriptionsWalked: number;
	entitlementsRecomputed: number;
	sessionsRescued: number;
	sessionsExpired: number;
	eventsRequeued: number;
	deadDeliveries: number;
	resyncsEnqueued: number;
	refundsRetried: number;
	staleChangesFailed: number;
	/** §9.3's family invariant — customers found holding two live tiers of one family. */
	familyDuplicatesFound: number;
	familyDuplicatesCancelled: number;
	/** §10.1 — live subscriptions whose billing tax rate was checked against the table. */
	taxRatesExamined: number;
	/** Those found billing at the wrong rate and rewritten at the provider. */
	taxRatesUpdated: number;
	orphanedRefs: number;
	prunedEventPayloads: number;
	prunedDeliveryPayloads: number;
	prunedEntitlementChanges: number;
	/**
	 * §12.8's analytics retention, which is two settings and therefore three figures.
	 *
	 * Anonymised is rows whose IP and user agent were nulled while the row itself stayed; archived
	 * is rows written into the daily archive before their events went; deleted is what actually
	 * went. Separate because they answer separate questions — personal data at rest, and volume —
	 * and one combined figure would make a store doing both look identical to one doing neither.
	 */
	analyticsAnonymised: number;
	analyticsArchived: number;
	analyticsDeleted: number;
	/** The delete hit its per-pass bound, so the night left work behind and the next pass continues. */
	analyticsDeleteCapped: boolean;
	oldestUnprocessedEventAt: string | null;
	oldestPendingDeliveryAt: string | null;
	oldestPendingEmailAt: string | null;
	anomalies: string[];
}

export interface ReconciliationRun {
	uuid: string;
	kind: 'minutes' | 'nightly';
	status: 'running' | 'completed' | 'failed';
	startedAt: string;
	finishedAt: string | null;
	durationMs: number | null;
	report: ReconciliationReport | null;
	error: string | null;
}

export interface ReconciliationHistory {
	latest: ReconciliationRun | null;
	items: ReconciliationRun[];
}

// -------------------------------------------------------------------------------------------
// Catalog sync status (Design §5.3)
// -------------------------------------------------------------------------------------------

/** `never_synced` is the state a catalog object is in before it has ever been pushed. */
export type CatalogObjectSyncStatus = 'synced' | 'pending' | 'failed' | 'never_synced';

export interface CatalogSyncStatusItem {
	objectType: 'product' | 'price' | 'coupon' | 'customer';
	identifier: string;
	name: string;
	syncStatus: CatalogObjectSyncStatus;
	syncError: string | null;
	providerObjectID: string | null;
	lastAttemptAt: string | null;
	syncedAt: string | null;
}

export interface CatalogSyncStatus {
	providerUUID: string | null;
	providerName: string | null;
	items: CatalogSyncStatusItem[];
	/** Anything not `synced`. The one number the admin badge is driven from. */
	outOfDate: number;
}

// -------------------------------------------------------------------------------------------
// Manual entitlement (Design §4.4, §13)
// -------------------------------------------------------------------------------------------

/** A comp. The note is mandatory: an entitlement granted for no stated reason is unauditable. */
export interface EntitlementGrantInput {
	entitlementKey: string;
	productSlug?: string | null;
	expiresAt?: string | null;
	note: string;
}

export interface EntitlementGrantResult {
	grantID: number;
	/** How many entitlement change rows the grant produced — zero means nothing moved. */
	changes: number;
}

export interface EntitlementRevokeResult {
	changes: number;
}

/** One AbstractID application, as the registration picker needs it. */
export interface AbstractIDApplication {
	uuid: string;
	name: string;
	isActive: boolean;
	description: string | null;
	iconURL: string | null;
}

/**
 * The directory is **optional**: it answers `available: false` with a reason rather than an error
 * status, because a picker is a convenience over a field that still works. Failing the request
 * would take the registration screen down when only the lookup was unavailable.
 */
export interface AbstractIDDirectory {
	available: boolean;
	reason: string | null;
	items: AbstractIDApplication[];
}

/** Where a simulated clock is: settled, still moving towards `advancingTo`, or broken at the provider. */
export type TestClockStatus = 'ready' | 'advancing' | 'internal_failure';

/** A customer on a test clock — a Store customer where one holds the provider id, else the id alone. */
export interface TestClockCustomer {
	providerCustomerID: string;
	userUUID: string | null;
	displayName: string | null;
	email: string | null;
}

/** One simulated clock on a test-mode provider (Stripe's test clocks). */
export interface TestClock {
	id: string;
	name: string | null;
	/** The instant everything attached to the clock believes it is. */
	frozenTime: string;
	status: TestClockStatus;
	advancingTo: string | null;
	/** When the provider deletes the clock, and every customer on it, by itself. */
	deletesAfter: string;
	customers: TestClockCustomer[];
}

/** A provider's clocks, or — `supported: false` — the sentence saying why it has none. */
export interface TestClockOverview {
	supported: boolean;
	reason: string | null;
	clocks: TestClock[];
}

/** One test-mode provider as seen from a customer's page. */
export interface CustomerTestClock {
	provider: { uuid: string; name: string };
	/** Null where the customer has never been created at this provider; attaching creates them. */
	providerCustomerID: string | null;
	clock: TestClock | null;
	error: string | null;
}

/** One of a provider's test cards, applied to a subscription in one click (test mode only). */
export interface TestCardPreset {
	key: string;
	label: string;
	description: string;
	brand: string;
	last4: string;
}

/** A stored card as the admin card chooser draws it. */
export interface AdminPaymentMethod {
	uuid: string;
	type: string | null;
	brand: string | null;
	last4: string | null;
	expiryMonth: number | null;
	expiryYear: number | null;
	isDefault: boolean;
	wallet: string | null;
	nickname: string | null;
}

/** The cards one subscription could bill on, the one it does, and the test cards on offer. */
export interface SubscriptionPaymentMethods {
	subscriptionUUID: string;
	provider: { uuid: string; name: string; mode: PaymentProviderMode };
	canChange: boolean;
	reason: string | null;
	currentPaymentMethodUUID: string | null;
	methods: AdminPaymentMethod[];
	testCards: TestCardPreset[];
}

/** What a re-read found: how many subscriptions were checked and what had moved. */
export interface ResyncResult {
	checked: number;
	corrected: number;
	anomalies: string[];
}

/** What webhook provisioning did. The signing secret itself is never returned. */
export interface WebhookProvisionResult {
	endpointID: string;
	url: string;
	created: boolean;
	/**
	 * False when the provider only reveals a secret at creation and the endpoint already existed —
	 * the stored secret is kept rather than blanked.
	 */
	secretStored: boolean;
	events: string[];
}

/**
 * What repricing existing subscribers on a price would do, before anything is written (§9.8).
 *
 * `increases` is stated separately because a rise is the regulated case: an admin who cannot see
 * how many people are about to pay more cannot judge whether the notice they are about to send is
 * proportionate.
 */
export interface RepricePreview {
	fromPriceUUID: string;
	toPriceUUID: string;
	affected: number;
	increases: number;
	decreases: number;
	unchanged: number;
	currency: string;
	fromAmountMinor: number | null;
	toAmountMinor: number | null;
	/** Days of advance notice every affected customer gets. Floored at 30 server-side. */
	noticeDays: number;
	/** The earliest any leg could land. Each customer's own leg lands on their own renewal. */
	earliestEffectiveAt: string;
}

export interface RepriceResult {
	batchUUID: string;
	/** Customers sent a notice. The reprice is real for exactly these. */
	notified: number;
	skipped: number;
	anomalies: string[];
}

/**
 * A legal entity invoices are issued by (§4.6).
 *
 * Everything here appears on the document and most of it is required to: Article 226 of the VAT
 * Directive lists the seller's full name, full address and VAT identification number, and a German
 * invoice additionally carries the commercial register entry and the tax number.
 */
export interface Seller {
	uuid: string;
	name: string;
	legalName: string;
	addressLine1: string | null;
	addressLine2: string | null;
	addressPostalCode: string | null;
	addressCity: string | null;
	addressCountry: string | null;
	vatID: string | null;
	taxNumber: string | null;
	registrationNumber: string | null;
	registrationCourt: string | null;
	email: string | null;
	phone: string | null;
	website: string | null;
	footerNote: string | null;
	/**
	 * A closing line printed under the totals of every invoice this entity issues.
	 *
	 * Separate from `footerNote` because the two are read differently: the footer is the legal band
	 * — bank details, a registration number, a payment term — and this is addressed to the customer.
	 * Invoices only; a credit note reverses a sale, and thanking somebody for business on it reads
	 * as a store that does not know what it has just sent.
	 */
	invoiceMessage: string | null;
	/** Prefixes every number this entity issues, so two entities never share a series. */
	invoiceNumberPrefix: string;
	/** The seller for every product that names none. At most one, enforced in the database. */
	isDefault: boolean;
	isActive: boolean;
	/** How many products name it explicitly, which is what makes deactivation answerable. */
	productCount: number;
	/** The logo printed at the head of its documents — PNG or JPEG — or null. */
	logoUUID: string | null;
	logoURL: string | null;
	/** Where this year's two number series stand. */
	numbering: { year: number; invoice: SellerSeriesPosition; creditNote: SellerSeriesPosition };
}

export interface SellerSeriesPosition {
	/** 0 where nothing has been issued in the series this year. */
	lastIssued: number;
	next: number;
}

export type SellerWriteInput = Partial<Omit<Seller, 'uuid' | 'isDefault' | 'productCount' | 'logoURL' | 'numbering'>>;

/**
 * One jurisdiction's rate in the fallback tax table (§10.1).
 *
 * Only in force when the payment provider is not calculating tax itself. `basisPoints` rather than
 * a percentage, and an integer: 2550 is 25.5%, and a rate held as a float is a hair off on every
 * invoice it touches.
 */
/**
 * What a country does with a cross-border sale, beyond the rate (§10.1).
 *
 * `standard` charges the row's rate. The other two reach zero **on purpose**, which is why each
 * requires a sentence for the invoice: a zero with nothing beside it is read as tax that was owed
 * and missed, and only the document can say which of the three reasons produced it.
 */
export type TaxMode = 'standard' | 'reverse_charge' | 'no_tax';

export interface TaxRate {
	country: string;
	/** A sub-national jurisdiction, or empty for the country-wide rate. */
	region: string;
	/** The rate charged to a consumer. */
	basisPoints: number;
	/**
	 * The rate charged to a business — a customer who gave a company name.
	 *
	 * A separate figure rather than a rule over the first, because the two genuinely differ by
	 * jurisdiction. Not the same test as reverse charge, which turns on a VAT ID.
	 */
	businessBasisPoints: number;
	/** What appears on the invoice — "German VAT". A record has to name the tax it charged. */
	name: string;
	/**
	 * What this country does with a sale to a **consumer**, beyond the rate. A business is read
	 * from `businessMode`, chosen by the same company-name test as the rate.
	 *
	 * Honoured on **cross-border** sales only: the seller's own country always charges its rate,
	 * because home is the one jurisdiction it is unambiguously liable in. It sits above the built-in
	 * EU reverse-charge rule rather than replacing it — that rule is about the seller, and an EU
	 * store keeps it whether or not anybody has opened this screen.
	 */
	mode: TaxMode;
	/**
	 * The sentence printed on the invoice when that mode takes effect.
	 *
	 * Required by a mode that charges nothing and refused by `standard`, both ways round: a mode
	 * with no sentence leaves an unexplained zero on a legal document, and a sentence on a
	 * `standard` row is text the screen shows and nothing ever prints.
	 */
	invoiceNote: string | null;
	/**
	 * What this country does with a sale to a **business**. Separate from `mode` because a
	 * jurisdiction routinely reverse-charges a registered business while charging a consumer its
	 * standard rate, and one mode for both was wrong for whichever side it was not typed for.
	 */
	businessMode: TaxMode;
	/** `invoiceNote`'s twin for the business side, paired with `businessMode` the same way. */
	businessInvoiceNote: string | null;
	/** §10.1 — whether a declared professional with no company is charged as a business here. */
	allowProfessionalIndividuals?: boolean;
	/** §10.1 — sell here to registered businesses only: a VAT ID is required, and reverse charge applies. */
	requireTaxID?: boolean;
	isActive: boolean;
}

/**
 * The rates plus where the store still cannot sell (§10.1).
 *
 * Every active rate is charged, so the gap worth reporting is the opposite one: a country the
 * seller is liable in with no rate on file refuses checkout outright. From the server's side that
 * is one correct error at one customer's checkout — the admin sees nothing, and the customer sees
 * only a checkout that will not proceed.
 */
export interface TaxCoverageResponse extends ListResponse<TaxRate> {
	/** The country the store sells from, as `tax.sellerCountry` resolves it. */
	sellerCountry: string;
	sellerInEU: boolean;
	/** No active rate for the seller's own country: domestic sales are refused outright. */
	sellerRateMissing: boolean;
	/** EU member states with no active rate. Only populated for an EU seller, which OSS makes
	 * liable across the union whether or not anybody entered a number. */
	uncoveredEUStates: string[];
}

/**
 * The format a tax ID must match for one country and type (§10.1). `pattern` is a regular expression
 * applied to the number in upper case with spaces and separators removed, as a full match; `example`
 * is a specimen that matches it, shown to a customer whose number is refused.
 */
export interface TaxIDFormat {
	country: string;
	taxIDType: string;
	pattern: string;
	example: string;
}

/** What is written for one tax ID format. The country and type travel in the path. */
export interface TaxIDFormatWriteInput {
	pattern: string;
	example: string;
}

export interface TaxRateWriteInput {
	basisPoints: number;
	/** Omitted means "the same as the consumer rate" — never zero, which would untax businesses. */
	businessBasisPoints?: number;
	name: string;
	/** Omitted means `standard`, so an unrelated edit cannot stop a country charging tax. */
	mode?: TaxMode;
	/** Required by a non-standard mode, refused by `standard`. */
	invoiceNote?: string | null;
	/** Omitted means "the same as `mode`", note included — which is what one mode meant. */
	businessMode?: TaxMode;
	/** Required by a non-standard business mode, refused by `standard`. */
	businessInvoiceNote?: string | null;
	allowProfessionalIndividuals?: boolean;
	requireTaxID?: boolean;
	isActive?: boolean;
}

// ---------------------------------------------------------------------------------------------
// Analytics (Design §12)
//
// Every shape below is what one admin panel renders. Money is a **list keyed by currency** on all
// of them, never a scalar: minor units in two currencies are different units, and a single summed
// figure is wrong in a way that still looks like money.
// ---------------------------------------------------------------------------------------------

export interface AnalyticsCurrencyAmount {
	currency: string;
	amount: number;
}

export interface AnalyticsFunnelStage {
	stage: number;
	key: string;
	label: string;
	sessions: number;
	/** Of the stage before it, as a fraction. 1 at the first stage, which has nothing to divide by. */
	conversionFromPrevious: number;
	dropOff: number;
}

/**
 * How much of a range the raw events can still answer for (§12.8).
 *
 * Raw events are deleted at `analytics.eventRetentionDays`; the daily archive takes their place but
 * keeps counts rather than rows. A range reaching past the cutoff is one the store can answer only
 * partly, and the panels have to say which part — reporting the shortfall as zero makes a quarter
 * the store was trading through read as a quarter nobody visited.
 */
export interface AnalyticsRetentionCoverage {
	cutoff: string;
	partial: boolean;
	/** The metrics still answerable past the cutoff. Everything else is declared missing, not guessed. */
	archivedMetrics: string[];
}

export interface AnalyticsOverview {
	range: { from: string; to: string };
	retention: AnalyticsRetentionCoverage;
	previous: { from: string; to: string };
	visitors: number;
	sessions: number;
	pageViews: number;
	productViews: number;
	engagedSessions: number;
	checkoutSessions: number;
	purchases: number;
	conversionRate: number;
	revenue: AnalyticsCurrencyAmount[];
	funnel: AnalyticsFunnelStage[];
	/** What the bot exclusion is holding back — stated, so a default never hides without saying so. */
	botSessionsExcluded: number;
	/** Completed checkouts with no journey behind them: a store that switched collection on late. */
	unattributedPurchases: number;
	comparison: {
		visitors: number;
		sessions: number;
		purchases: number;
		revenue: AnalyticsCurrencyAmount[];
	};
}

export type AnalyticsMetric =
	| 'sessions'
	| 'visitors'
	| 'pageViews'
	| 'productViews'
	| 'engaged'
	| 'checkouts'
	| 'purchases'
	| 'revenue';

export interface AnalyticsSeries {
	/** Null for a count; a money metric is one series per currency and never one summed series. */
	currency: string | null;
	points: { bucket: string; value: number }[];
}

export interface AnalyticsTimeseries {
	metric: AnalyticsMetric;
	granularity: string;
	retention: AnalyticsRetentionCoverage;
	series: AnalyticsSeries[];
	previousSeries: AnalyticsSeries[];
}

export interface AnalyticsBreakdownRow {
	value: string | null;
	label: string;
	events: number;
	sessions: number;
	/** Null on an event dimension: a pageview does not convert, and a zero there would say it did. */
	purchases: number | null;
	revenue: AnalyticsCurrencyAmount[] | null;
}

export interface AnalyticsBreakdown {
	dimension: string;
	scope: 'event' | 'session';
	items: AnalyticsBreakdownRow[];
	total: number;
	/**
	 * Events the question does not apply to — a `catalog_view` names no product, a `page_view` names
	 * no currency. Stated rather than bucketed as "Unknown", which is what made these panels report
	 * their largest bar as a category that is not one. Always zero on a session dimension.
	 */
	notApplicable: number;
}

export interface AnalyticsProductRow {
	productUUID: string;
	productName: string;
	familySlug: string | null;
	views: number;
	viewingSessions: number;
	engagedSessions: number;
	checkouts: number;
	purchases: number;
	conversionRate: number;
	revenue: AnalyticsCurrencyAmount[];
}

export interface AnalyticsAbandonmentExample {
	sessionUUID: string;
	startedAt: string;
	lastEventAt: string;
	maxStage: number;
	userEmail: string | null;
	country: string | null;
	deviceType: string | null;
	lastPath: string | null;
	/** The condition of the last refusal this customer was shown, which is usually the answer. */
	lastErrorCondition: string | null;
	productName: string | null;
}

export interface AnalyticsAbandonmentKind {
	key: 'browse' | 'flow' | 'payment';
	label: string;
	denominatorLabel: string;
	abandoned: number;
	denominator: number;
	rate: number;
	examples: AnalyticsAbandonmentExample[];
}

export interface AnalyticsAbandonment {
	kinds: AnalyticsAbandonmentKind[];
	/** Visits still inside their idle window, counted in none of the three. */
	sessionsInProgress: number;
	errorConditions: { condition: string; count: number }[];
}

export interface AnalyticsSessionRow {
	sessionUUID: string;
	visitorUUID: string;
	startedAt: string;
	lastEventAt: string;
	eventCount: number;
	maxStage: number;
	userEmail: string | null;
	country: string | null;
	deviceType: string | null;
	browser: string | null;
	referrerHost: string | null;
	utmSource: string | null;
	entryPath: string | null;
	exitPath: string | null;
	isBot: boolean;
	/** Still going, by the store's own idle window — answered by the server, never guessed here. */
	inProgress: boolean;
	/** First event to last, in seconds. Zero for a visit that produced one event. */
	durationSeconds: number;
}

export interface AnalyticsSessionEvent {
	id: number;
	type: string;
	receivedAt: string;
	path: string | null;
	productName: string | null;
	currency: string | null;
	properties: Record<string, unknown>;
}

export interface AnalyticsSessionDetail {
	session: AnalyticsSessionRow;
	events: AnalyticsSessionEvent[];
	purchases: { uuid: string; kind: 'order' | 'subscription'; currency: string; amount: number }[];
}

export interface AnalyticsVisitorDetail {
	visitorUUID: string;
	firstSeenAt: string;
	lastSeenAt: string;
	sessionCount: number;
	eventCount: number;
	landingPath: string | null;
	referrerHost: string | null;
	utmSource: string | null;
	utmMedium: string | null;
	utmCampaign: string | null;
	/** A list, because §12.1's stitch is many-to-many: a shared machine really is two people. */
	linkedUsers: { userUUID: string; email: string | null; displayName: string | null }[];
	sessions: AnalyticsSessionRow[];
}

export interface AnalyticsFilterOptions {
	products: { uuid: string; name: string; slug: string }[];
	families: { slug: string; name: string }[];
	countries: string[];
	devices: string[];
	browsers: string[];
	operatingSystems: string[];
	sources: string[];
	mediums: string[];
	campaigns: string[];
	referrers: string[];
	currencies: string[];
}

/** The filter every analytics panel shares, and the query string it becomes. */
export interface AnalyticsQuery {
	from?: string;
	to?: string;
	granularity?: 'day' | 'week' | 'month';
	identity?: 'any' | 'anonymous' | 'known';
	includeBots?: boolean;
	productUUID?: string;
	familySlug?: string;
	/**
	 * One customer's journeys, by the **user's** uuid — the same identifier the admin customer page
	 * is addressed by, so support arrives here from there rather than at a second screen.
	 */
	userUUID?: string;
	currency?: string;
	country?: string;
	deviceType?: string;
	browser?: string;
	os?: string;
	source?: string;
	medium?: string;
	campaign?: string;
	referrerHost?: string;
	path?: string;
}

/**
 * One row of a `SelectField`.
 *
 * `value` is what is stored and sent, `name` is what is read — and they are separate for the reason
 * every picker in this app keeps them separate: the wire value is `eu_vat` and the thing a customer
 * recognises is "VAT number (EU)". `group` draws a heading above the first row carrying it, so rows
 * sharing one must be adjacent; `description` is the muted second line for a row whose name is not
 * the whole story.
 */
export interface SelectOption {
	value: string;
	name: string;
	group?: string;
	description?: string;
	disabled?: boolean;
}

// ---------------------------------------------------------------------------------------------
// Financial reports (Design §13)
//
// Beside analytics rather than inside it, and the split is by source as much as by question:
// analytics reads the visit log and answers "what did people do", these read invoices, orders and
// refunds — the money's own tables — and answer "what did the store take". Every shape here is a
// **list keyed by currency** and never a scalar, for the reason nothing in this app ever sums minor
// units across currencies: it is arithmetically valid, semantically meaningless, and the result
// looks entirely plausible.
// ---------------------------------------------------------------------------------------------

/**
 * The movements a store's money makes.
 *
 * Each is a genuinely different question rather than a slice of one. A renewal and a first sale are
 * both money in and are read completely differently — one is growth, one is retention — and an
 * `adjustment` is what a plan change bills, which is the one "sale" routinely negative.
 */
export type ReportEventKind = 'sale' | 'renewal' | 'adjustment' | 'refund' | 'chargeback';

/** What a report may group by. Mapped to a column server-side; the client never names one. */
export type ReportDimension =
	| 'kind'
	| 'currency'
	| 'product'
	| 'family'
	| 'customer'
	| 'country'
	| 'seller'
	| 'provider'
	| 'billing'
	| 'interval'
	| 'coupon';

/** How the individual movements are ordered. */
export type ReportSort = 'recent' | 'oldest' | 'largest' | 'smallest';

/** The one filter every report panel sends, mirrored into the URL so a slice can be shared. */
export interface ReportQuery {
	from?: string;
	to?: string;
	granularity?: 'day' | 'week' | 'month';
	/** Several, because "revenue" and "money back out" are each more than one kind. */
	kind?: ReportEventKind[];
	currency?: string;
	productUUID?: string;
	familySlug?: string;
	userUUID?: string;
	country?: string;
	sellerUUID?: string;
	providerUUID?: string;
	couponCode?: string;
	minAmount?: number;
	search?: string;
	sort?: ReportSort;
}

/** One currency's slice of a report. */
export interface ReportCurrencyTotals {
	currency: string;
	gross: number;
	/** A positive magnitude — what an operator reads as "refunds". */
	refunded: number;
	chargebacks: number;
	tax: number;
	/** `gross − refunded − chargebacks`. What the store kept. */
	net: number;
	netExcludingTax: number;
	movements: number;
}

export interface ReportKindTotals {
	kind: ReportEventKind;
	currency: string;
	/** Signed: refunds and chargebacks are negative. */
	amount: number;
	tax: number;
	net: number;
	movements: number;
}

export interface ReportSummary {
	range: { from: string; to: string };
	totals: ReportCurrencyTotals[];
	/** The same window immediately before, so a figure reads as a movement. */
	previous: ReportCurrencyTotals[];
	byKind: ReportKindTotals[];
	movements: number;
}

export interface ReportTimeseriesPoint {
	bucket: string;
	currency: string;
	kind: ReportEventKind;
	amount: number;
	net: number;
	movements: number;
}

export interface ReportTimeseries {
	range: { from: string; to: string };
	granularity: 'day' | 'week' | 'month';
	/** Every bucket in the window, empty ones included, so a chart has an x axis. */
	buckets: string[];
	points: ReportTimeseriesPoint[];
}

export interface ReportBreakdownRow {
	key: string | null;
	label: string | null;
	currency: string;
	gross: number;
	refunded: number;
	chargebacks: number;
	net: number;
	movements: number;
}

export interface ReportBreakdown {
	dimension: ReportDimension;
	rows: ReportBreakdownRow[];
	/** Rows past the limit, so a truncated table says it is truncated. */
	remaining: number;
}

/** One movement of money, as the report lists it. */
export interface ReportEvent {
	occurredAt: string;
	kind: ReportEventKind;
	sourceKind: string;
	sourceUUID: string;
	/** Ours where we issued one, never the provider's beside it (§4.6). */
	reference: string;
	currency: string;
	/** Signed, in minor units. */
	amount: number;
	tax: number;
	net: number;
	quantity: number;
	customerName: string | null;
	customerEmail: string | null;
	userUUID: string | null;
	country: string | null;
	productName: string | null;
	productUUID: string | null;
	familyName: string | null;
	familySlug: string | null;
	interval: string | null;
	couponCode: string | null;
	sellerName: string | null;
	providerName: string | null;
	subscriptionUUID: string | null;
	orderUUID: string | null;
	invoiceUUID: string | null;
	creditNoteUUID: string | null;
	creditNoteNumber: string | null;
}

export interface ReportEventPage {
	items: ReportEvent[];
	total: number;
	/** Of **everything the filter matches**, not of the page — see the server's own note. */
	totals: ReportCurrencyTotals[];
}

/**
 * One issued document — an invoice or a credit note — as the admin document list carries it.
 *
 * `ours` is what decides everything on the row: whether there is a PDF of ours to download, and
 * whether the bulk archive can contain it at all. A document the payment provider issued lives at
 * the provider and is offered as a link instead — ours or theirs, never both.
 */
export interface ReportDocument {
	kind: 'invoice' | 'credit_note';
	uuid: string;
	reference: string;
	ours: boolean;
	issuedAt: string | null;
	currency: string;
	/** Signed: a credit note's total is negative, as the document states it. */
	total: number;
	status: string | null;
	customerName: string | null;
	customerEmail: string | null;
	userUUID: string | null;
	productName: string | null;
	sellerName: string | null;
	hostedURL: string | null;
	pdfURL: string | null;
}

export interface ReportDocumentPage {
	items: ReportDocument[];
	total: number;
	/** How many of them the Store issued, which is how many the archive would contain. */
	ours: number;
}

/**
 * One invoice, as the store-wide register at `/Invoices` lists it (§4.6).
 *
 * **It extends the support screens' `AdminInvoice` rather than sitting beside it**, because the
 * overlapping half is the same document read by the same person for the same reason — and two
 * declarations of it are two shapes free to drift, at which point the customer page and the
 * register disagree about an invoice while both render something plausible. What the register adds
 * is who the invoice belongs to, which a list already scoped to one customer never needed and this
 * one is useless without.
 *
 * Everything here is rendered through `invoiceReference`, `invoiceStatusLabel` and
 * `InvoiceDocumentLinks`, exactly as `AccountInvoice` is: there are two number columns and only one
 * of them is on the paper the customer holds, and a refund leaves the provider's status at `paid`
 * forever.
 */
export interface AdminInvoiceRecord extends AdminInvoice {
	/** Why the provider raised it (§9.6) — a first purchase, the period coming round, a change. */
	billingReason: string | null;
	subtotal: number | null;
	tax: number | null;
	amountRemaining: number | null;
	periodStart: string | null;
	periodEnd: string | null;
	/** The provider's own copy — **withheld once the Store has issued its own document** (§4.6). */
	hostedInvoiceURL: string | null;
	invoicePDFURL: string | null;
	creditNotes: InvoiceCreditNote[];
	customerName: string | null;
	customerEmail: string | null;
	/** The **user's** uuid, which is how a customer is addressed on the admin side. */
	userUUID: string | null;
	country: string | null;
	familyName: string | null;
	sellerName: string | null;
	providerName: string | null;
}

/** One currency's slice of the register. Never summed across currencies. */
export interface AdminInvoiceTotals {
	currency: string;
	invoices: number;
	/** Billed, whether or not anybody has paid it. */
	billed: number;
	paid: number;
	/** Still owing, counted only on invoices awaiting payment. */
	outstanding: number;
	/** Gone back out, as a positive magnitude. */
	refunded: number;
}

export interface AdminInvoicePage {
	items: AdminInvoiceRecord[];
	total: number;
	/** Of everything the filter matches, never of the page in hand. */
	totals: AdminInvoiceTotals[];
}

/**
 * The register's query: the reports filter, plus the one thing the ledger has no concept of.
 *
 * An invoice's *status* cannot be asked of the reports page at all — its ledger only knows invoices
 * that were paid — and it is the whole reason the register exists as a screen of its own.
 */
export interface AdminInvoiceQuery extends ReportQuery {
	status?: string;
}

/** What the filter bar offers, read from the ledger rather than from the catalogue. */
export interface ReportFilterOptions {
	currencies: string[];
	products: { uuid: string; name: string }[];
	families: { slug: string; name: string }[];
	countries: string[];
	sellers: { uuid: string; name: string }[];
	providers: { uuid: string; name: string }[];
	coupons: string[];
	kinds: ReportEventKind[];
}

/**
 * §9.6 — the self-serve refund window, as `/Settings/Refunds` edits it. A purchase's
 * window is read product → billing country → currency → `defaultWindowDays`.
 */
export interface RefundPolicy {
	defaultWindowDays: number;
	countries: { country: string; refundWindowDays: number }[];
	currencies: { code: string; name: string; symbol: string; isEnabled: boolean; refundWindowDays: number | null }[];
}
